import React, { useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import register from "../../assets/register.png";
import Loading from "../Loading";
import useFormValidation from "../../hooks/useFormValidation";
import PasswordService from "../../services/passwordService";

function PasswordResetRequest() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const validationRules = {
    email: {
      required: true,
      email: true,
      requiredMessage: "Email address is required",
      emailMessage: "Please enter a valid email address",
    },
  };

  const {
    formData,
    errors: formErrors,
    isSubmitting,
    handleChange: handleInputChange,
    handleSubmit,
    setIsSubmitting,
  } = useFormValidation(
    {
      email: "",
    },
    validationRules
  );

  const onSubmit = useCallback(async (data) => {
    setIsLoading(true);
    
    try {
      const result = await PasswordService.initiatePasswordReset(data.email);

      if (result.success) {
        toast.success("Password reset PIN sent to your email!");
        navigate("/password-reset-confirm", { 
          state: { 
            email: data.email,
            maskedEmail: result.data.email,
            patientName: result.data.patient_name,
            hospitalName: result.data.hospital_name,
            expiresAt: result.data.expires_at
          } 
        });
      } else {
        toast.error(result.message || "Failed to send reset PIN");
      }
    } catch (error) {
      if (error.message) {
        console.log('error.message', error);
        toast.error(error?.response?.data?.message || error.message, {
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      } else {
        toast.error("Network error. Please try again.");
      }
    } finally {
      setIsLoading(false);
      setIsSubmitting(false);
    }
  }, [navigate, setIsSubmitting]);

  const formSubmitHandler = handleSubmit(onSubmit);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 flex flex-col justify-center items-center px-4 py-8">
      <div className="w-full max-w-md sm:max-w-lg lg:max-w-xl bg-white rounded-2xl shadow-2xl overflow-hidden">
        
        <div 
          className="relative h-48 sm:h-56 lg:h-64 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url(${register})`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-primary100/80 to-emerald-700/80">
            <div className="absolute bottom-6 left-6">
              <h1 className="text-white text-2xl sm:text-3xl lg:text-4xl font-bold">
                Reset Password
              </h1>
              <p className="text-white/90 text-lg sm:text-xl">
                for Promise
              </p>
            </div>
          </div>
        </div>

        <div className="px-6 py-8">
          <div className="mb-6">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">
              Forgot Your Password?
            </h2>
            <p className="text-gray-600 text-sm sm:text-base">
              Enter your file number or phone number and we'll send you a mail to reset your password.
            </p>
          </div>

          <form onSubmit={formSubmitHandler} className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                File number or phone number
              </label>
              <input
                type="text"
                name="email"
                value={formData.email}
                placeholder="Enter you file number or phone number"
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary100 focus:border-primary100 transition-all duration-200 text-gray-800 placeholder-gray-400 ${
                  formErrors.email ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
                required
                disabled={isSubmitting || isLoading}
              />
              {formErrors.email && (
                <p className="mt-1 text-sm text-red-600">{formErrors.email}</p>
              )}
            </div>

            <div className="space-y-4">
              {isLoading || isSubmitting ? (
                <Loading fullScreen={false} message="Sending Reset PIN..." variant="medical" size="medium" />
              ) : (
                <button
                  type="submit"
                  disabled={isSubmitting || isLoading}
                  className="w-full py-4 bg-primary100 hover:bg-emerald-700 disabled:bg-gray-400 disabled:cursor-not-allowed rounded-xl text-white text-lg font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                >
                  Send Reset PIN
                </button>
              )}
            </div>
          </form>

          <div className="mt-8 pt-6 border-t border-gray-100 text-center">
            <p className="text-gray-600 text-sm">
              Remember your password?{' '}
              <Link 
                to="/login" 
                className="text-primary100 font-semibold hover:text-emerald-700 transition-colors duration-200"
              >
                Back to Login
              </Link>
            </p>
          </div>

          <div className="mt-6 flex justify-center">
            <div className="flex items-center space-x-2 text-xs text-gray-500">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span>Secure Reset Process</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PasswordResetRequest;